import {
  ArrowRight,
  ArrowSquareOut,
  BracketsCurly,
  Broadcast,
  Check,
  CirclesFour,
  Copy,
  Database,
  GlobeHemisphereWest,
  Lightning,
  List,
  MagnifyingGlass,
  ShieldCheck,
  SquaresFour,
  X,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { BrandLogo } from "./components/BrandLogo";
import { NetworkVisual } from "./components/NetworkVisual";
import { SetupPanel } from "./components/SetupPanel";
import { ToolsSection } from "./components/ToolsSection";
import { coverageItems, ENDPOINT, faqs, type Locale } from "./data";

const coverageIcons = [ShieldCheck, SquaresFour, Lightning, Broadcast, CirclesFour, GlobeHemisphereWest];

const copy = {
  en: {
    nav: ["Setup", "Tools", "FAQ"],
    copyEndpoint: "Copy endpoint",
    copied: "Copied",
    heroTitleA: "BlockRazor knowledge,",
    heroTitleB: "inside your coding agent.",
    heroBody: "Connect your MCP client to current BlockRazor documentation, integration guides, API references, and source-linked answers.",
    connect: "Connect MCP",
    docs: "View documentation",
    setupTitle: "Setup",
    setupBody: "Add the public BlockRazor MCP endpoint to your client, then verify that four documentation tools are available.",
    toolsTitle: "Tools",
    toolsBody: "Explore all four tools and copy a ready-to-use request for each one.",
    coverageTitle: "Knowledge coverage",
    coverageBody: "Connected to the official BlockRazor documentation and integration guides.",
    faqTitle: "FAQ",
    finalTitle: "Bring BlockRazor knowledge into your coding workflow.",
    finalBody: "Connect once, then build with current documentation and source-linked answers.",
    footerTitle: "Navigate Your Transactions",
    footerBody: "Global lighting-speed multi-chain network",
    product: "Product",
    resources: "Resources",
  },
  zh: {
    nav: ["设置", "工具", "常见问题"],
    copyEndpoint: "复制端点",
    copied: "已复制",
    heroTitleA: "让 BlockRazor 知识",
    heroTitleB: "进入你的编码 Agent。",
    heroBody: "将 MCP 客户端连接到当前 BlockRazor 文档、集成指南、API 参考与带来源的答案。",
    connect: "连接 MCP",
    docs: "查看文档",
    setupTitle: "设置",
    setupBody: "将公开 BlockRazor MCP 端点添加到客户端，并确认四个文档工具均已加载。",
    toolsTitle: "工具",
    toolsBody: "查看四个工具，并复制每个工具对应的可用请求示例。",
    coverageTitle: "知识覆盖",
    coverageBody: "连接 BlockRazor 官方文档与产品集成指南。",
    faqTitle: "常见问题",
    finalTitle: "把 BlockRazor 知识带入你的编码工作流。",
    finalBody: "一次连接，即可使用当前文档与带来源的答案进行开发。",
    footerTitle: "驾驭你的交易",
    footerBody: "全球光速多链网络",
    product: "产品",
    resources: "资源",
  },
} as const;

function App() {
  const [locale, setLocale] = useState<Locale>("en");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const t = copy[locale];

  useEffect(() => {
    document.documentElement.lang = locale === "en" ? "en" : "zh-CN";
  }, [locale]);

  const copyText = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey(null), 1800);
  };

  const scrollToSetup = () => document.getElementById("setup")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="site-shell" id="top">
      <header className="site-header">
        <div className="header-inner">
          <BrandLogo />
          <nav className={mobileOpen ? "site-nav open" : "site-nav"} aria-label="Primary navigation">
            {[
              ["setup", t.nav[0]],
              ["tools", t.nav[1]],
              ["faq", t.nav[2]],
            ].map(([id, label]) => (
              <a href={`#${id}`} key={id} onClick={() => setMobileOpen(false)}>
                {label}
              </a>
            ))}
            <button className="mobile-language" onClick={() => setLocale(locale === "en" ? "zh" : "en")} type="button">
              {locale === "en" ? "切换到中文" : "Switch to English"}
            </button>
          </nav>
          <div className="header-actions">
            <button className="language-control" onClick={() => setLocale(locale === "en" ? "zh" : "en")} type="button">
              {locale === "en" ? "EN / 中文" : "中文 / EN"}
            </button>
            <button className="endpoint-button" onClick={() => copyText(ENDPOINT, "header-endpoint")} type="button">
              {copiedKey === "header-endpoint" ? <Check size={17} /> : <Copy size={17} />}
              {copiedKey === "header-endpoint" ? t.copied : t.copyEndpoint}
            </button>
            <button
              className="mobile-menu"
              onClick={() => setMobileOpen(!mobileOpen)}
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={23} /> : <List size={23} />}
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="hero-section">
          <div className="star-field" aria-hidden="true" />
          <div className="page-width hero-grid">
            <div className="hero-copy">
              <h1>
                {t.heroTitleA}
                <span>{t.heroTitleB}</span>
              </h1>
              <p>{t.heroBody}</p>
              <div className="hero-actions">
                <button className="primary-action" onClick={scrollToSetup} type="button">
                  {t.connect}
                  <ArrowRight size={18} />
                </button>
                <a className="secondary-action" href="https://docs.blockrazor.io/" target="_blank" rel="noreferrer">
                  {t.docs}
                  <ArrowSquareOut size={17} />
                </a>
              </div>
            </div>
            <NetworkVisual />
          </div>

          <div className="page-width status-rail">
            <div className="status-item online">
              <span className="status-dot" aria-hidden="true" />
              <div><strong>{locale === "en" ? "Available" : "服务可用"}</strong><small>{locale === "en" ? "Public documentation endpoint" : "公开文档端点"}</small></div>
            </div>
            <div className="status-item"><MagnifyingGlass size={19} /><div><strong>{ENDPOINT}</strong><small>Endpoint</small></div></div>
            <div className="status-item"><Database size={19} /><div><strong>Streamable HTTP</strong><small>Transport</small></div></div>
            <div className="status-item"><BracketsCurly size={19} /><div><strong>MCP 2025-03-26</strong><small>Protocol</small></div></div>
            <div className="status-item"><SquaresFour size={19} /><div><strong>4 tools</strong><small>{locale === "en" ? "Documentation access" : "文档能力"}</small></div></div>
          </div>
        </section>

        <section className="content-section page-width" id="setup">
          <div className="section-intro">
            <h2>{t.setupTitle}</h2>
            <p>{t.setupBody}</p>
          </div>
          <SetupPanel locale={locale} copyText={copyText} copiedKey={copiedKey} />
        </section>

        <section className="content-section page-width" id="tools">
          <div className="section-intro">
            <h2>{t.toolsTitle}</h2>
            <p>{t.toolsBody}</p>
            <a href="https://docs.blockrazor.io/" target="_blank" rel="noreferrer">
              {locale === "en" ? "Open documentation" : "打开文档"}<ArrowRight size={16} />
            </a>
          </div>
          <ToolsSection locale={locale} copyText={copyText} copiedKey={copiedKey} />
        </section>

        <section className="coverage-section" id="coverage">
          <div className="page-width coverage-layout">
            <div className="section-intro">
              <h2>{t.coverageTitle}</h2>
              <p>{t.coverageBody}</p>
              <a href="https://docs.blockrazor.io/get-started/supported-chains" target="_blank" rel="noreferrer">
                {locale === "en" ? "Explore documentation" : "浏览文档"}<ArrowRight size={16} />
              </a>
            </div>
            <div className="coverage-grid">
              {coverageItems.map((item, index) => {
                const Icon = coverageIcons[index];
                return (
                  <article key={item.title.en}>
                    <Icon size={29} />
                    <div><h3>{item.title[locale]}</h3><p>{item.body[locale]}</p></div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="faq-section page-width" id="faq">
          <div className="section-intro"><h2>{t.faqTitle}</h2></div>
          <div className="faq-list">
            {faqs.map((item, index) => (
              <article className={openFaq === index ? "faq-item open" : "faq-item"} key={item.q.en}>
                <button onClick={() => setOpenFaq(openFaq === index ? -1 : index)} type="button" aria-expanded={openFaq === index}>
                  <span>{item.q[locale]}</span><span aria-hidden="true">+</span>
                </button>
                <div className="faq-answer"><p>{item.a[locale]}</p></div>
              </article>
            ))}
          </div>
        </section>

        <section className="final-cta page-width">
          <div>
            <h2>{t.finalTitle}</h2>
            <p>{t.finalBody}</p>
          </div>
          <div className="hero-actions">
            <button className="primary-action" onClick={scrollToSetup} type="button">{t.connect}<ArrowRight size={18} /></button>
            <a className="secondary-action" href="https://docs.blockrazor.io/" target="_blank" rel="noreferrer">{t.docs}<ArrowSquareOut size={17} /></a>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="page-width footer-grid">
          <div className="footer-brand"><BrandLogo compact /><strong>{t.footerTitle}</strong><p>{t.footerBody}</p></div>
          <div><strong>{t.product}</strong><a href="#setup">MCP Setup</a><a href="#tools">Tool Reference</a><a href="https://docs.blockrazor.io/" target="_blank" rel="noreferrer">Documentation</a></div>
          <div><strong>{t.resources}</strong><a href="https://github.com/BlockRazorinc" target="_blank" rel="noreferrer">GitHub</a><a href="https://discord.gg/qqJuwRb8Nh" target="_blank" rel="noreferrer">Discord</a><a href="https://blockrazor.io/" target="_blank" rel="noreferrer">BlockRazor.io</a></div>
          <div className="footer-meta"><span><i aria-hidden="true" />{locale === "en" ? "Endpoint available" : "端点可用"}</span><small>© 2026 BlockRazor</small></div>
        </div>
      </footer>
    </div>
  );
}

export default App;
